import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { getAdminLook, putAdminLook } from "../../api/platformSettings";
import { ColorModeContext, useMode } from "../../theme";
import { LookContext } from "./lookContext";
import { AdminLook, LOOKS } from "./looks";
import { ensureLookFont, readCachedLook, resolveLook, writeCachedLook } from "./lookState";

export { useAdminLook, useLookTokens } from "./lookContext";

/**
 * The platform look + the operator's light / dark, as the MUI theme. Sits
 * inside `RequireAuth` (the GET needs the token); the cached look paints
 * first, so a reload shows no flash of the wrong look.
 */
export function LookProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState(() => ({ look: readCachedLook(), settingAvailable: false }));

  useEffect(() => {
    let alive = true;
    void resolveLook(getAdminLook).then((s) => {
      if (alive) setState(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    ensureLookFont(LOOKS[state.look]);
  }, [state.look]);

  const setLook = useCallback(async (look: AdminLook) => {
    const saved = await putAdminLook(look);
    writeCachedLook(saved);
    setState((s) => ({ ...s, look: saved }));
  }, []);

  const value = useMemo(
    () => ({ look: state.look, settingAvailable: state.settingAvailable, setLook }),
    [state, setLook],
  );
  const [theme, colorMode] = useMode(state.look);

  return (
    <LookContext.Provider value={value}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </ColorModeContext.Provider>
    </LookContext.Provider>
  );
}
